import { database } from './db';

// One object store per kind of data, so each can be cleared, or kept, on its own. Inside a store every
// field has its own key, e.g. the auth store has the keys token, refreshToken and user.

/** Who is signed in: the keys token, refreshToken (renews the token) and user. Cleared when they sign out. */
const AUTH_STORE = 'auth';
/** Which business the session works with: the key business (or businesses and activeBusinessId in admin). Cleared when they sign out. */
const BUSINESS_STORE = 'business';
/** Who signed in last on this device: the keys businessCode, businessName and username. Kept when they sign out. */
const LAST_SIGN_IN_STORE = 'last-sign-in';

/** Saves each field of `record` under its own key. Fields that are undefined are not saved, and keys no longer in the record are removed. */
async function saveFields<TRecord extends object>(
	storeName: string,
	record: TRecord,
): Promise<void> {
	const entries = Object.fromEntries(
		Object.entries(record).filter(([, value]) => value !== undefined),
	);
	await database.replaceAll({ storeName, entries });
}

/** Reads a store's keys back into one object. Null when the store is empty. */
async function readFields<TRecord>(storeName: string): Promise<TRecord | null> {
	const entries = await database.getAll({ storeName });
	return Object.keys(entries).length > 0 ? (entries as TRecord) : null;
}

export async function saveAuth<TAuth extends object>(
	auth: TAuth,
): Promise<void> {
	await saveFields(AUTH_STORE, auth);
}

export async function getAuth<TAuth>(): Promise<TAuth | null> {
	return readFields<TAuth>(AUTH_STORE);
}

export async function saveBusinessData<TBusinessData extends object>(
	businessData: TBusinessData,
): Promise<void> {
	await saveFields(BUSINESS_STORE, businessData);
}

export async function getBusinessData<
	TBusinessData,
>(): Promise<TBusinessData | null> {
	return readFields<TBusinessData>(BUSINESS_STORE);
}

/** Everything about the signed-in session: the auth and business stores. The last sign-in is left alone so the sign-in page can still ask for just a password. */
export async function clearSessionData(): Promise<void> {
	await Promise.all([
		database.clear({ storeName: AUTH_STORE }),
		database.clear({ storeName: BUSINESS_STORE }),
	]);
}

export async function saveLastSignIn<TLastSignIn extends object>(
	lastSignIn: TLastSignIn,
): Promise<void> {
	await saveFields(LAST_SIGN_IN_STORE, lastSignIn);
}

export async function getLastSignIn<
	TLastSignIn,
>(): Promise<TLastSignIn | null> {
	return readFields<TLastSignIn>(LAST_SIGN_IN_STORE);
}

export async function clearLastSignIn(): Promise<void> {
	await database.clear({ storeName: LAST_SIGN_IN_STORE });
}
